/**
 * Thomson's basemag history against Kp and Ap, as data for the Space weather verdict (decisions 704, 719, 749, 750;
 * docs/research/2026-10-27-basemag-history.md §3 and §6; derived numbers in
 * tests/fixtures/space-weather/basemag-history/station-days.csv, which `test/space-weather.test.ts` checks this
 * table against, sensor by sensor). Usable Australian station-days, 19 Feb to 26 Oct 2026, each judged with Teslyn's
 * own `windowMetric` under three rules side by side. Each rule counts the bases that fly it (decision 749): the
 * 10 nT 10-minute chord rule every sensor (407 days: MagStation, G-823 and G-857); Plot 1 and Plot 2 of the tight
 * specifications only MagStation and G-823 bases (196 days), never a G-857. The levels themselves are MB's
 * (`OUTLOOK_THRESHOLDS`, decision 750); this table gives the words beside them ("38% of 79 station-days like this
 * went over ...").
 *
 * Per-project quiet-day baselines and BoM local K bands are follow-ups (research §6 recommendations 3 and 4).
 */

/** The three rules of the history (research §6, a to c). */
export type HistoryRuleId = 'chord10' | 'plot1Tight' | 'plot2Tight';

export type KpBand = '0-2' | '3' | '4' | '5' | '6+';
export type ApBand = '0-7' | '8-15' | '16-29' | '30-49' | '50+';

/** The base sensors of the history (`station-days.csv` column `sensor`; research §6 "Which sensor"). */
export type HistorySensor = 'magstation' | 'g823_probable' | 'g857';

/** One band of one rule: how many station-days, the share with any time over, and how much time. */
export interface HistoryBand {
  readonly stationDays: number;
  /** Share of station-days with any reading over the limit, 0 to 1. */
  readonly shareOver: number;
  /** Median minutes over, among the days that had any. */
  readonly medianMinOver: number;
  /** Median share of the trimmed day over the limit, over all days in the band, %. */
  readonly medianDayLostPct: number;
}

export interface HistoryRule {
  readonly id: HistoryRuleId;
  /** The rule in words, as the research note names it. */
  readonly label: string;
  readonly windowMin: number;
  readonly limitNt: number;
  readonly mode: 'peakToPeak' | 'deviationFromChord';
  /** The base sensors whose station-days the rule counts (decision 749). */
  readonly sensors: readonly HistorySensor[];
  /** The bases in words, for the screen ("MagStation and G-823 bases"). */
  readonly sensorWords: string;
  /** Usable Australian station-days of those sensors. */
  readonly stationDays: number;
  readonly byKp: Readonly<Record<KpBand, HistoryBand>>;
  readonly byAp: Readonly<Record<ApBand, HistoryBand>>;
}

const b = (
  stationDays: number,
  sharePct: number,
  medianMinOver: number,
  medianDayLostPct: number,
): HistoryBand => ({
  stationDays,
  shareOver: sharePct / 100,
  medianMinOver,
  medianDayLostPct,
});

const TIGHT_SENSORS: readonly HistorySensor[] = ['magstation', 'g823_probable'];

/**
 * Australia, by the highest Kp of the readings (rounded) and by daily Ap (research §6 tables "Australia, MagStation +
 * G-823 (probable)", 28 Oct 2026; the chord rule over every sensor).
 */
export const BASEMAG_HISTORY_AU: Readonly<Record<HistoryRuleId, HistoryRule>> = {
  chord10: {
    id: 'chord10',
    label: '10 nT from a 10-minute chord',
    windowMin: 10,
    limitNt: 10,
    mode: 'deviationFromChord',
    sensors: ['magstation', 'g823_probable', 'g857'],
    sensorWords: 'MagStation, G-823 and G-857 bases',
    stationDays: 407,
    byKp: {
      '0-2': b(170, 10, 10, 0),
      '3': b(122, 15, 10, 0),
      '4': b(64, 12, 4, 0),
      '5': b(35, 20, 6, 0),
      '6+': b(16, 62, 6, 0.2),
    },
    byAp: {
      '0-7': b(187, 11, 10, 0),
      '8-15': b(143, 16, 6, 0),
      '16-29': b(51, 16, 6, 0),
      '30-49': b(22, 32, 8, 0),
      '50+': b(4, 50, 21, 0.2),
    },
  },
  plot1Tight: {
    id: 'plot1Tight',
    label: 'peak to peak 7.5 nT in 5 min',
    windowMin: 5,
    limitNt: 7.5,
    mode: 'peakToPeak',
    sensors: TIGHT_SENSORS,
    sensorWords: 'MagStation and G-823 bases',
    stationDays: 196,
    byKp: {
      '0-2': b(79, 15, 5, 0),
      '3': b(63, 22, 5, 0),
      '4': b(32, 22, 12, 0),
      '5': b(13, 54, 11, 0.1),
      '6+': b(9, 67, 80, 3),
    },
    byAp: {
      '0-7': b(95, 18, 5, 0),
      '8-15': b(65, 28, 6, 0),
      '16-29': b(28, 18, 11, 0),
      '30-49': b(7, 71, 13, 1.3),
      '50+': b(1, 100, 188, 20.1),
    },
  },
  plot2Tight: {
    id: 'plot2Tight',
    label: 'peak to peak 1.5 nT in 1 min',
    windowMin: 1,
    limitNt: 1.5,
    mode: 'peakToPeak',
    sensors: TIGHT_SENSORS,
    sensorWords: 'MagStation and G-823 bases',
    stationDays: 196,
    byKp: {
      '0-2': b(79, 38, 3, 0),
      '3': b(63, 60, 4, 0.1),
      '4': b(32, 72, 3, 0.2),
      '5': b(13, 77, 11, 0.9),
      '6+': b(9, 100, 28, 3.3),
    },
    byAp: {
      '0-7': b(95, 43, 3, 0),
      '8-15': b(65, 69, 4, 0.2),
      '16-29': b(28, 57, 6, 0.1),
      '30-49': b(7, 100, 19, 2.7),
      '50+': b(1, 100, 285, 30.4),
    },
  },
};

/** Bands with fewer station-days than this are "indicative" (Plot 1 and Plot 2: 13 at Kp 5, 9 at Kp 6+). */
export const HISTORY_INDICATIVE_BELOW = 30;

/** The band of a Kp value (the history rounds the highest Kp to a whole number). */
export function kpBand(kp: number): KpBand {
  const k = Math.round(kp);
  return k <= 2 ? '0-2' : k === 3 ? '3' : k === 4 ? '4' : k === 5 ? '5' : '6+';
}

export function apBand(ap: number): ApBand {
  return ap < 8 ? '0-7' : ap < 16 ? '8-15' : ap < 30 ? '16-29' : ap < 50 ? '30-49' : '50+';
}

/**
 * The history rule nearest a project limit: a 1- or 2-minute window is Plot 2's 1.5 nT in 1 min; a peak-to-peak
 * window up to 7 minutes is Plot 1's 7.5 nT in 5 min; anything else is the 10 nT 10-minute chord rule.
 */
export function historyRuleFor(limit: { readonly windowMin: number; readonly mode: string }): HistoryRuleId {
  if (limit.windowMin <= 2) return 'plot2Tight';
  if (limit.mode === 'peakToPeak' && limit.windowMin <= 7) return 'plot1Tight';
  return 'chord10';
}

/** "Kp 5: 37% of 35 station-days like this went over, median 6 min (0.0% of the day)". */
export function historyBandWords(rule: HistoryRule, band: KpBand): string {
  const h = rule.byKp[band];
  const share = Math.round(h.shareOver * 100);
  const bases = rule.sensors.includes('g857') ? '' : 'MagStation and G-823 ';
  return `Kp ${band}: ${share}% of ${h.stationDays} ${bases}station-days like this went over, median ${h.medianMinOver} min when they did (${h.medianDayLostPct.toFixed(1)}% of the median day)${h.stationDays < HISTORY_INDICATIVE_BELOW ? '; few days, indicative only' : ''}`;
}
